import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  CircularProgress, 
  Container, 
  Divider, 
  Grid, 
  TextField, 
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Save, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import shipmentService from '../../services/shipmentService';
import { usersApi } from '../../services/api';
import { getDrivers } from '../../services/driverService';
import zoneService from '../../services/zoneService';
import { CreateShipmentDto } from '../../types/shipment';

type CreateShipmentFormData = {
  origin: string;
  destination: string;
  weight: string | number;
  customer_id: number | undefined;
  driver_id: number | null;
  zone_id: number | null;
  status: string;
  notes: string | null;
};

const schema = yup.object().shape({
  origin: yup
    .string()
    .required('Origin is required')
    .max(255, 'Origin must be at most 255 characters'),
  destination: yup
    .string()
    .required('Destination is required')
    .max(255, 'Destination must be at most 255 characters'),
  weight: yup
    .number()
    .typeError('Weight must be a number')
    .required('Weight is required')
    .positive('Weight must be greater than 0')
    .max(1000, 'Weight must be at most 1000'),
  customer_id: yup
    .number()
    .typeError('Customer must be a number')
    .required('Customer is required')
    .integer('Customer must be an integer'),
  driver_id: yup
    .number()
    .nullable()
    .transform((value) => (isNaN(value) ? null : value)),
  zone_id: yup
    .number()
    .nullable()
    .transform((value) => (isNaN(value) ? null : value)),
  status: yup
    .string()
    .required('Status is required')
    .oneOf(['pending', 'in_transit', 'out_for_delivery', 'delivered', 'exception', 'returned'], 'Invalid status'),
  notes: yup
    .string()
    .nullable()
    .max(1000, 'Notes must be at most 1000 characters'),
});

const CreateShipmentForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState<boolean>(false);
  const [customers, setCustomers] = useState<Array<{ id: number; name: string }>>([]);
  const [drivers, setDrivers] = useState<Array<{ id: string; name: string }>>([]);
  const [zones, setZones] = useState<Array<{ id: number; name: string }>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { control, handleSubmit, formState: { errors }, setValue, reset } = useForm<CreateShipmentFormData>({
    resolver: yupResolver(schema) as any, // Type assertion to handle yup resolver type issues
    defaultValues: {
      origin: '',
      destination: '',
      weight: '',
      customer_id: undefined,
      driver_id: null,
      zone_id: null,
      status: 'pending',
      notes: null
    },
    mode: 'onChange'
  });

  // Fetch customers, drivers, and zones
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [customersData, driversData, zonesData] = await Promise.all([
          usersApi.getAll(),
          getDrivers(),
          zoneService.getZones(),
        ]);

        setCustomers(customersData.map((c: any) => ({ id: c.id, name: c.name || '' })));
        setDrivers(driversData.map((d: any) => ({ id: String(d.id), name: d.name || '' })));
        setZones(zonesData.map((z: any) => ({ id: z.id, name: z.name })));
      } catch (error) {
        console.error('Error fetching data:', error);
        // On error, reset state to prevent crashes
        setCustomers([]);
        setDrivers([]);
        setZones([]);
      }
    };

    fetchData();
  }, []);

  const onSubmit: SubmitHandler<CreateShipmentFormData> = async (data) => {
    try {
      setIsSubmitting(true);
      setError('');
      setSuccess('');
      
      // Prepare the shipment data
      const shipmentData: CreateShipmentDto = {
        ...data,
        weight: Number(data.weight),
        customer_id: Number(data.customer_id),
        driver_id: data.driver_id ? Number(data.driver_id) : null,
        zone_id: data.zone_id ? Number(data.zone_id) : null,
        notes: data.notes || null,
      };
      
      console.log('[CreateShipmentForm] Submitting shipment:', shipmentData);
      
      // Call the API
      const response = await shipmentService.createShipment(shipmentData);
      console.log('[CreateShipmentForm] Shipment created:', response);
      
      // Show success message
      setSuccess(t('shipments.createSuccess'));
      
      // Reset form
      reset();
      
      // Redirect to shipments list after a short delay
      setTimeout(() => {
        navigate('/shipments');
      }, 2000);
      
    } catch (error) {
      const errorMessage = error instanceof Error 
        ? error.message 
        : t('errors.unexpectedError');
        
      console.error('[CreateShipmentForm] Error creating shipment:', error);
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box my={4}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/shipments')}
          sx={{ mb: 2 }}
        >
          {t('common.back')}
        </Button>

        <Typography variant="h4" component="h1" gutterBottom>
          {t('shipments.create.title')}
        </Typography>
        
        <Card>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="origin"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label={t('shipments.fields.origin')}
                        variant="outlined"
                        error={!!errors.origin}
                        helperText={errors.origin?.message}
                        margin="normal"
                        required
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="destination"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label={t('shipments.fields.destination')}
                        variant="outlined"
                        error={!!errors.destination}
                        helperText={errors.destination?.message}
                        margin="normal"
                        required
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="weight"
                    control={control}
                    render={({ field: { onChange, value, ...field } }) => {
                      // Convert value to string for the input
                      const inputValue = value === undefined || value === null ? '' : String(value);
                      
                      return (
                        <TextField
                          {...field}
                          value={inputValue}
                          onChange={(e) => {
                            const val = e.target.value;
                            // Allow empty string or valid numbers with optional decimal
                            if (val === '' || /^\d*\.?\d*$/.test(val)) {
                              onChange(val);
                            }
                          }}
                          onBlur={() => {
                            // Convert to number and back to string to normalize the format
                            const num = parseFloat(inputValue);
                            if (!isNaN(num)) {
                              onChange(String(num));
                            }
                          }}
                          fullWidth
                          type="text"
                          inputMode="decimal"
                          label={t('shipments.fields.weight')}
                          variant="outlined"
                          error={!!errors.weight}
                          helperText={errors.weight?.message}
                          margin="normal"
                          required
                          InputProps={{
                            endAdornment: 'kg'
                          }}
                        />
                      );
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="customer_id"
                    control={control}
                    render={({ field }) => (
                      <FormControl 
                        fullWidth 
                        margin="normal"
                        error={!!errors.customer_id}
                        required
                      >
                        <InputLabel id="customer-label">
                          {t('shipments.fields.customer')}
                        </InputLabel>
                        <Select
                          {...field}
                          labelId="customer-label"
                          label={t('shipments.fields.customer')}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          {customers.map((customer) => (
                            <MenuItem key={customer.id} value={customer.id}>
                              {customer.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.customer_id && (
                          <FormHelperText>{errors.customer_id.message}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="driver_id"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth margin="normal">
                        <InputLabel id="driver-label">
                          {t('shipments.fields.driver')} ({t('common.optional')})
                        </InputLabel>
                        <Select
                          {...field}
                          labelId="driver-label"
                          label={`${t('shipments.fields.driver')} (${t('common.optional')})`}
                          onChange={(e) => field.onChange(e.target.value)}
                          value={field.value || ''}
                        >
                          <MenuItem value="">
                            <em>{t('common.none')}</em>
                          </MenuItem>
                          {drivers.map((driver) => (
                            <MenuItem key={driver.id} value={driver.id}>
                              {driver.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="zone_id"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth margin="normal">
                        <InputLabel id="zone-label">
                          {t('shipments.fields.zone')} ({t('common.optional')})
                        </InputLabel>
                        <Select
                          {...field}
                          labelId="zone-label"
                          label={`${t('shipments.fields.zone')} (${t('common.optional')})`}
                          onChange={(e) => field.onChange(e.target.value)}
                          value={field.value || ''}
                        >
                          <MenuItem value="">
                            <em>{t('common.none')}</em>
                          </MenuItem>
                          {zones.map((zone) => (
                            <MenuItem key={zone.id} value={zone.id}>
                              {zone.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <FormControl 
                        fullWidth 
                        margin="normal"
                        error={!!errors.status}
                        required
                      >
                        <InputLabel id="status-label">
                          {t('shipments.fields.status')}
                        </InputLabel>
                        <Select
                          {...field}
                          labelId="status-label"
                          label={t('shipments.fields.status')}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <MenuItem value="pending">{t('shipments.status.pending')}</MenuItem>
                          <MenuItem value="in_transit">{t('shipments.status.in_transit')}</MenuItem>
                          <MenuItem value="out_for_delivery">{t('shipments.status.out_for_delivery')}</MenuItem>
                          <MenuItem value="delivered">{t('shipments.status.delivered')}</MenuItem>
                          <MenuItem value="exception">{t('shipments.status.exception')}</MenuItem>
                          <MenuItem value="returned">{t('shipments.status.returned')}</MenuItem>
                        </Select>
                        {errors.status && (
                          <FormHelperText>{errors.status.message}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="notes"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        rows={4}
                        label={t('shipments.fields.notes')}
                        variant="outlined"
                        margin="normal"
                        placeholder={t('shipments.notesPlaceholder')}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/shipments')}
                      disabled={loading}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                      disabled={loading}
                    >
                      {loading ? t('common.creating') : t('common.create')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default CreateShipmentForm;
